import { formatUserDisplayName } from '../../lib/formatUserDisplayName';
import { distinctTeams, mergeTeamOptions } from '../../lib/teams';
import type { CurrentUser } from '../../types/user';

// Slice B §5.2: a single combined picker for "assigned to" — People OR
// Teams OR Unassigned, mutually exclusive (model A). Picking a person sets
// assignedUserId + clears assignedTeam; picking a team does the inverse;
// Unassigned clears both. The caller PATCHes the returned pair so the
// either/or invariant is always satisfied (the server re-enforces it).
//
// Option values are prefixed so the parse is unambiguous:
//   ''        -> Unassigned
//   'u:<uid>' -> a user (uid never contains ':')
//   'g:<team>'-> a team (split once on ':' so team names with ':' survive)
export type AssignmentValue = {
  assignedUserId: string | null;
  assignedTeam: string | null;
};

function encode(v: AssignmentValue): string {
  if (v.assignedUserId) return `u:${v.assignedUserId}`;
  if (v.assignedTeam) return `g:${v.assignedTeam}`;
  return '';
}

function decode(raw: string): AssignmentValue {
  if (raw.startsWith('u:')) {
    return { assignedUserId: raw.slice(2), assignedTeam: null };
  }
  if (raw.startsWith('g:')) {
    return { assignedUserId: null, assignedTeam: raw.slice(2) };
  }
  return { assignedUserId: null, assignedTeam: null };
}

export function TaskAssignmentPicker({
  users,
  assignedUserId,
  assignedTeam,
  disabled,
  onChange,
  className,
}: {
  users: CurrentUser[];
  assignedUserId: string | null;
  assignedTeam: string | null;
  disabled?: boolean;
  onChange: (next: AssignmentValue) => void;
  className?: string;
}) {
  // Include the current team even if no User.team matches it — a team
  // applied from a template's defaultTeam (§5.5) must stay selected/visible.
  const teams = mergeTeamOptions(distinctTeams(users), assignedTeam);
  const current = encode({ assignedUserId, assignedTeam });

  return (
    <select
      value={current}
      disabled={disabled}
      onChange={(e) => onChange(decode(e.target.value))}
      className={
        className ??
        'w-full rounded border border-slate-300 px-2 py-1 text-sm'
      }
    >
      <option value="">— Unassigned —</option>
      {users.length > 0 && (
        <optgroup label="People">
          {users.map((u) => (
            <option key={u.uid} value={`u:${u.uid}`}>
              {formatUserDisplayName(u, { full: true })}
            </option>
          ))}
        </optgroup>
      )}
      {teams.length > 0 && (
        <optgroup label="Teams">
          {teams.map((t) => (
            <option key={t} value={`g:${t}`}>
              {t}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );
}

// Pure label for list/Build-View rendering (§5.4): user name if assigned to
// a person, team name if assigned to a team, else "Unassigned".
export function assignmentLabel(
  users: CurrentUser[],
  assignedUserId: string | null,
  assignedTeam: string | null,
): string {
  if (assignedUserId) {
    const u = users.find((x) => x.uid === assignedUserId);
    return u ? formatUserDisplayName(u, { full: true }) : 'Unknown user';
  }
  if (assignedTeam) return assignedTeam;
  return 'Unassigned';
}
